import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';
import { AvatarContainer } from '../components/AvatarContainer';
import { AudioButton } from '../components/AudioButton';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { DashboardSidebar, MobileBottomNav } from '../components/dashboard/DashboardNav';
import { QuickActionsGrid, SecondaryActionsGrid } from '../components/dashboard/DashboardActions';
import { DailyQuests } from '../components/DailyQuests';
import { DailyGrammarChallenge } from '../components/DailyGrammarChallenge';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Flame, Star, Target, ChevronRight, Zap, BookOpen,
  Trophy, Sparkles, Mic, BarChart3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const levelColors = { A1: 'bg-teal-500', A2: 'bg-blue-500', B1: 'bg-purple-500', B2: 'bg-amber-500' };

export default function Dashboard() {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [badges, setBadges] = useState([]);
  const [dailyStats, setDailyStats] = useState(null);
  const [courseReport, setCourseReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [progressRes, badgesRes, dailyRes, reportRes] = await Promise.all([
          axios.get(`${API}/progress`, { withCredentials: true }),
          axios.get(`${API}/badges/user`, { withCredentials: true }),
          axios.get(`${API}/daily-stats`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/progress/report`, { withCredentials: true }).catch(() => ({ data: null }))
        ]);
        setProgress(progressRes.data);
        setBadges(badgesRes.data.earned || []);
        setDailyStats(dailyRes.data);
        setCourseReport(reportRes.data);
      } catch (error) {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => { await logout(); navigate('/'); };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <DailyGrammarChallenge />
      <DashboardSidebar user={user} onLogout={handleLogout} />

      <main className="lg:ml-64 min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <h1 className="font-heading text-2xl font-bold">{t('welcomeBack')}, {user?.name?.split(' ')[0]}!</h1>
              <p className="text-muted-foreground text-sm">{t('continueLearning')}</p>
            </div>
            <div className="flex items-center gap-4">
              <LanguageSelector />
              <div className="flex items-center gap-2 bg-amber-500/10 px-3 py-1.5 rounded-full">
                <Flame className="h-4 w-4 text-amber-500 streak-flame" />
                <span className="font-mono font-bold text-amber-600">{user?.streak_days || 0}</span>
              </div>
            </div>
          </div>
        </header>

        <div className="p-6">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => <Skeleton key={`skel-${i}`} className="h-32 rounded-xl" />)}
            </div>
          ) : (
            <>
              {/* Stats Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <StatsCard label={t('currentLevel')} value={user?.current_level || 'A1'} color="teal" icon={<Target className="h-6 w-6 text-white" />} bgClass={levelColors[user?.current_level] || 'bg-teal-500'} />
                <StatsCard label={t('totalPoints')} value={user?.total_points?.toLocaleString() || 0} color="amber" icon={<Star className="h-6 w-6 text-white" />} mono />
                <StatsCard label={t('streakDays')} value={user?.streak_days || 0} color="orange" icon={<Flame className="h-6 w-6 text-white" />} mono />
                <StatsCard label={t('lessonsCompleted')} value={`${progress?.lessons?.completed || 0}/${progress?.lessons?.total || 0}`} color="purple" icon={<BookOpen className="h-6 w-6 text-white" />} mono />
              </div>

              {/* Level Progress & AI Teachers */}
              <div className="grid lg:grid-cols-3 gap-6 mb-8">
                <Card className="lg:col-span-2">
                  <CardHeader><CardTitle className="font-heading">{t('yourProgress')}</CardTitle></CardHeader>
                  <CardContent className="space-y-6">
                    {progress?.level_progress && Object.entries(progress.level_progress).map(([level, data]) => (
                      <div key={level}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge className={levelColors[level]}>{level}</Badge>
                            <span className="text-sm text-muted-foreground">{data.completed}/{data.total} lessons</span>
                          </div>
                          <span className="font-mono text-sm font-bold">{data.percentage}%</span>
                        </div>
                        <Progress value={data.percentage} className="h-2" />
                      </div>
                    ))}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="font-heading">{t('aiTeachers')}</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-4 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer">
                      <AvatarContainer teacher="frau_rasha" size="sm" showName={false} />
                      <div><p className="font-semibold">{t('frauRasha')}</p><p className="text-sm text-muted-foreground">{t('frauRashaRole')}</p></div>
                    </div>
                    <div className="flex items-center gap-4 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer">
                      <AvatarContainer teacher="herr_amr" size="sm" showName={false} />
                      <div><p className="font-semibold">{t('herrAmr')}</p><p className="text-sm text-muted-foreground">{t('herrAmrRole')}</p></div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Course Progress */}
              {courseReport?.courses && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><BarChart3 className="h-5 w-5 text-teal-500" />Course Progress</h2>
                    <Link to="/my-progress" data-testid="view-full-progress"><Button variant="ghost" size="sm">Full Report <ChevronRight className="h-4 w-4 ml-1" /></Button></Link>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    {Object.entries(courseReport.courses).map(([id, course]) => {
                      const colors = { menschen_a11: 'border-t-blue-500', menschen_a12: 'border-t-indigo-500', pflege: 'border-t-cyan-500' };
                      return (
                        <Card key={id} className={`border-t-4 ${colors[id] || 'border-t-teal-500'}`} data-testid={`course-progress-${id}`}>
                          <CardContent className="pt-5">
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-semibold text-sm">{course.name}</p>
                              <span className="font-mono text-sm font-bold">{course.percentage}%</span>
                            </div>
                            <Progress value={course.percentage} className="h-2 mb-2" />
                            <p className="text-xs text-muted-foreground">{course.completed}/{course.total_lessons} lessons completed</p>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Daily Quests & Pronunciation */}
              <div className="grid lg:grid-cols-3 gap-6 mb-8">
                <div className="lg:col-span-2"><DailyQuests /></div>
                <Link to="/pronunciation-history" className="block" data-testid="pronunciation-history-link">
                  <Card className="h-full cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-t-4 border-t-teal-500">
                    <CardContent className="pt-6 flex flex-col items-center justify-center text-center h-full">
                      <div className="w-14 h-14 rounded-full bg-teal-500/10 flex items-center justify-center mb-3"><Mic className="h-7 w-7 text-teal-500" /></div>
                      <p className="font-semibold">Pronunciation History</p>
                      <p className="text-sm text-muted-foreground mt-1">Track your pronunciation progress</p>
                      <Button variant="ghost" size="sm" className="mt-3 text-teal-500">View Progress <ChevronRight className="h-4 w-4 ml-1" /></Button>
                    </CardContent>
                  </Card>
                </Link>
              </div>

              {/* Continue Learning & Badges */}
              <div className="grid lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="font-heading">{t('continueLearning')}</CardTitle>
                    <Link to="/lessons"><Button variant="ghost" size="sm">View all <ChevronRight className="h-4 w-4 ml-1" /></Button></Link>
                  </CardHeader>
                  <CardContent>
                    <Link to="/lessons" className="block">
                      <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-teal-500/10 to-teal-500/5 border border-teal-500/20 hover:border-teal-500/40 transition-colors">
                        <div className="w-12 h-12 rounded-full bg-teal-500 flex items-center justify-center"><Zap className="h-6 w-6 text-white" /></div>
                        <div className="flex-1"><p className="font-semibold">Start your next lesson</p><p className="text-sm text-muted-foreground">Continue where you left off</p></div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </Link>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="font-heading">{t('achievements')}</CardTitle>
                    <Link to="/achievements"><Button variant="ghost" size="sm">View all <ChevronRight className="h-4 w-4 ml-1" /></Button></Link>
                  </CardHeader>
                  <CardContent>
                    {badges.length > 0 ? (
                      <div className="grid grid-cols-3 gap-4">
                        {badges.slice(0, 3).map((badge) => (
                          <div key={badge.badge_id} className="badge-card text-center">
                            <span className="text-3xl mb-2">{badge.icon}</span>
                            <p className="text-sm font-medium">{badge.name_en}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Trophy className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>Complete lessons to earn badges!</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions Grids */}
              <QuickActionsGrid />
              <SecondaryActionsGrid dailyStats={dailyStats} />

              {/* Daily German Phrases */}
              <Card className="mt-6 border-t-4 border-t-teal-500">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-teal-500" />
                    <CardTitle className="font-heading">Daily German Phrases</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      { de: "Guten Morgen!", en: "Good morning!", ar: "صباح الخير!" },
                      { de: "Wie geht es Ihnen?", en: "How are you?", ar: "كيف حالك؟" },
                      { de: "Ich verstehe.", en: "I understand.", ar: "أنا أفهم." },
                      { de: "Können Sie das wiederholen?", en: "Can you repeat that?", ar: "هل يمكنك تكرار ذلك؟" },
                      { de: "Danke schön!", en: "Thank you!", ar: "شكراً جزيلاً!" },
                      { de: "Auf Wiedersehen!", en: "Goodbye!", ar: "مع السلامة!" }
                    ].map((phrase) => (
                      <div key={phrase.de} className="p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-teal-600 dark:text-teal-400">{phrase.de}</span>
                          <AudioButton text={phrase.de} />
                        </div>
                        <p className="text-sm text-muted-foreground">{phrase.en}</p>
                        <p className="text-sm text-muted-foreground/70" dir="rtl">{phrase.ar}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>

      <MobileBottomNav />
    </div>
  );
}

function StatsCard({ label, value, color, icon, bgClass, mono }) {
  return (
    <Card className={`border-t-4 border-t-${color}-500`}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className={`${mono ? 'font-mono' : 'font-heading'} text-3xl font-bold mt-1`}>{value}</p>
          </div>
          <div className={`w-12 h-12 rounded-full ${bgClass || `bg-${color}-500`} flex items-center justify-center`}>{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}
